import * as i from 'types';

import { carSelector } from 'services';

export const calculateFixedCosts = (
  cars: GatsbyTypes.ContentfulCars[],
  variables: GatsbyTypes.ContentfulVariables,
  period: 'year' | 'month',
  answers?: i.Answers,
) => {
  if (!answers)
    return {
      evPrice: 0,
      petrolPrice: 0,
    };

  const leaseCosts = calculateLease(cars, period, answers);
  const insuranceCosts = calculateInsurance(cars, period, answers);
  const maintenanceCosts = calculateMaintenance(cars, period, answers);
  const taxesCosts = calculateTaxes(cars, period, answers);

  if (answers.financing === 'operational') {
    return {
      evPrice: leaseCosts.evPrice,
      petrolPrice: leaseCosts.petrolPrice + taxesCosts,
    };
  } else {
    return {
      evPrice:
        leaseCosts.evPrice + insuranceCosts.evPrice + maintenanceCosts.evPrice,
      petrolPrice:
        leaseCosts.petrolPrice +
        insuranceCosts.petrolPrice +
        maintenanceCosts.petrolPrice +
        taxesCosts,
    };
  }
};

export const calculateLease = (
  cars: GatsbyTypes.ContentfulCars[],
  period: 'year' | 'month',
  answers?: i.Answers,
) => {
  if (!answers)
    return {
      evPrice: 0,
      petrolPrice: 0,
    };

  const { financing, fleet } = answers;
  let evPrice = 0;
  let petrolPrice = 0;

  if ((financing === 'financial' || financing === 'operational') && fleet) {
    const leasePrice =
      financing === 'financial'
        ? 'financialLeasePrice'
        : 'operationalLeasePrice';

    evPrice = Object.entries(fleet)
      .map(([key, value]) => {
        const car = carSelector(cars, key as keyof i.FleetInput, 'ev', true);
        const price = car?.[leasePrice];

        if (!price) return 0;

        const multiplier = period === 'year' ? 12 : 1;
        return price * value * multiplier;
      })
      .reduce((partial_sum, a) => partial_sum + a, 0);

    petrolPrice = Object.entries(fleet)
      .map(([key, value]) => {
        const car = carSelector(cars, key as keyof i.FleetInput, 'petrol');
        const price = car?.[leasePrice];

        if (!price) return 0;

        const multiplier = period === 'year' ? 12 : 1;
        return price * value * multiplier;
      })
      .reduce((partial_sum, a) => partial_sum + a, 0);
  }

  return {
    evPrice,
    petrolPrice,
  };
};

export const calculateLeaseIndiv = (
  cars: GatsbyTypes.ContentfulCars[],
  period: 'year' | 'month',
  answers?: i.Answers,
) => {
  const evPrice = {
    carOne: 0,
    carTwo: 0,
    carThree: 0,
    carFour: 0,
    carFive: 0,
    carSix: 0,
    vanOne: 0,
    vanTwo: 0,
    vanThree: 0,
    vanFour: 0,
    vanFive: 0,
    vanSix: 0,
  };
  const petrolPrice = {
    carOne: 0,
    carTwo: 0,
    carThree: 0,
    carFour: 0,
    carFive: 0,
    carSix: 0,
    vanOne: 0,
    vanTwo: 0,
    vanThree: 0,
    vanFour: 0,
    vanFive: 0,
    vanSix: 0,
  };

  if (!answers)
    return {
      evPrice,
      petrolPrice,
    };

  const { financing, fleet } = answers;

  if ((financing === 'financial' || financing === 'operational') && fleet) {
    const leasePrice =
      financing === 'financial'
        ? 'financialLeasePrice'
        : 'operationalLeasePrice';

    (Object.entries(fleet) as [i.KeyOfFleetInput, number][]).forEach(
      ([key, value]) => {
        const carEv = carSelector(cars, key, 'ev', true);
        const carPetrol = carSelector(cars, key, 'petrol');
        const priceEv = carEv?.[leasePrice];
        const pricePetrol = carPetrol?.[leasePrice];

        if (!priceEv || !pricePetrol) return 0;

        const multiplier = period === 'year' ? 12 : 1;
        evPrice[key] = priceEv * value * multiplier;
        petrolPrice[key] = pricePetrol * value * multiplier;
      },
    );
  }

  return {
    evPrice,
    petrolPrice,
  };
};

export const calculateInsurance = (
  cars: GatsbyTypes.ContentfulCars[],
  period: 'year' | 'month',
  answers?: i.Answers,
) => {
  if (!answers)
    return {
      evPrice: 0,
      petrolPrice: 0,
    };
  const { financing, fleet } = answers;
  let evPrice = 0;
  let petrolPrice = 0;

  if (financing !== 'operational' && fleet) {
    evPrice = Object.entries(fleet)
      .map(([key, value]) => {
        const car = carSelector(cars, key as keyof i.FleetInput, 'ev', true);

        if (!car?.insuranceCostsYearly) return 0;

        const divider = period === 'year' ? 1 : 12;
        return (car?.insuranceCostsYearly * value) / divider;
      })
      .reduce((partial_sum, a) => partial_sum + a, 0);

    petrolPrice = Object.entries(fleet)
      .map(([key, value]) => {
        const car = carSelector(cars, key as keyof i.FleetInput, 'petrol');

        if (!car?.insuranceCostsYearly) return 0;

        const divider = period === 'year' ? 1 : 12;
        return (car?.insuranceCostsYearly * value) / divider;
      })
      .reduce((partial_sum, a) => partial_sum + a, 0);
  }

  return {
    evPrice,
    petrolPrice,
  };
};

export const calculateInsuranceIndiv = (
  cars: GatsbyTypes.ContentfulCars[],
  period: 'year' | 'month',
  answers?: i.Answers,
) => {
  const evPrice = {
    carOne: 0,
    carTwo: 0,
    carThree: 0,
    carFour: 0,
    carFive: 0,
    carSix: 0,
    vanOne: 0,
    vanTwo: 0,
    vanThree: 0,
    vanFour: 0,
    vanFive: 0,
    vanSix: 0,
  };
  const petrolPrice = {
    carOne: 0,
    carTwo: 0,
    carThree: 0,
    carFour: 0,
    carFive: 0,
    carSix: 0,
    vanOne: 0,
    vanTwo: 0,
    vanThree: 0,
    vanFour: 0,
    vanFive: 0,
    vanSix: 0,
  };

  if (!answers)
    return {
      evPrice,
      petrolPrice,
    };

  const { financing, fleet } = answers;

  if (financing !== 'operational' && fleet) {
    (Object.entries(fleet) as [i.KeyOfFleetInput, number][]).forEach(
      ([key, value]) => {
        const carEv = carSelector(cars, key, 'ev', true);
        const carPetrol = carSelector(cars, key, 'petrol');

        if (!carEv?.insuranceCostsYearly || !carPetrol?.insuranceCostsYearly)
          return 0;

        const divider = period === 'year' ? 1 : 12;
        evPrice[key] = (carEv?.insuranceCostsYearly * value) / divider;
        petrolPrice[key] = (carPetrol?.insuranceCostsYearly * value) / divider;
      },
    );
  }
  return {
    evPrice,
    petrolPrice,
  };
};

export const calculateMaintenance = (
  cars: GatsbyTypes.ContentfulCars[],
  period: 'year' | 'month',
  answers?: i.Answers,
) => {
  if (!answers)
    return {
      evPrice: 0,
      petrolPrice: 0,
    };
  const { financing, fleet } = answers;
  let evPrice = 0;
  let petrolPrice = 0;

  if (financing !== 'operational' && fleet) {
    evPrice = Object.entries(fleet)
      .map(([key, value]) => {
        const car = carSelector(cars, key as keyof i.FleetInput, 'ev', true);

        if (!car?.maintenanceCosts) return 0;

        const divider = period === 'year' ? 1 : 12;
        return (car?.maintenanceCosts * value) / divider;
      })
      .reduce((partial_sum, a) => partial_sum + a, 0);

    petrolPrice = Object.entries(fleet)
      .map(([key, value]) => {
        const car = carSelector(cars, key as keyof i.FleetInput, 'petrol');

        if (!car?.maintenanceCosts) return 0;

        const divider = period === 'year' ? 1 : 12;
        return (car?.maintenanceCosts * value) / divider;
      })
      .reduce((partial_sum, a) => partial_sum + a, 0);
  }

  return {
    evPrice,
    petrolPrice,
  };
};

export const calculateMaintenanceIndiv = (
  cars: GatsbyTypes.ContentfulCars[],
  period: 'year' | 'month',
  answers?: i.Answers,
) => {
  const evPrice = {
    carOne: 0,
    carTwo: 0,
    carThree: 0,
    carFour: 0,
    carFive: 0,
    carSix: 0,
    vanOne: 0,
    vanTwo: 0,
    vanThree: 0,
    vanFour: 0,
    vanFive: 0,
    vanSix: 0,
  };
  const petrolPrice = {
    carOne: 0,
    carTwo: 0,
    carThree: 0,
    carFour: 0,
    carFive: 0,
    carSix: 0,
    vanOne: 0,
    vanTwo: 0,
    vanThree: 0,
    vanFour: 0,
    vanFive: 0,
    vanSix: 0,
  };

  if (!answers)
    return {
      evPrice,
      petrolPrice,
    };

  const { financing, fleet } = answers;

  if (financing !== 'operational' && fleet) {
    (Object.entries(fleet) as [i.KeyOfFleetInput, number][]).forEach(
      ([key, value]) => {
        const carEv = carSelector(cars, key, 'ev', true);
        const carPetrol = carSelector(cars, key, 'petrol');

        if (!carEv?.maintenanceCosts || !carPetrol?.maintenanceCosts) return 0;

        const divider = period === 'year' ? 1 : 12;
        evPrice[key] = (carEv?.maintenanceCosts * value) / divider;
        petrolPrice[key] = (carPetrol?.maintenanceCosts * value) / divider;
      },
    );
  }
  return {
    evPrice,
    petrolPrice,
  };
};

export const calculateTaxes = (
  cars: GatsbyTypes.ContentfulCars[],
  period: 'year' | 'month',
  answers?: i.Answers,
) => {
  if (!answers) return 0;
  const { fleet } = answers;

  if (fleet) {
    return Object.entries(fleet)
      .map(([key, value]) => {
        const car = carSelector(cars, key as keyof i.FleetInput, 'petrol');

        if (!car?.taxesCostsYearly) return 0;

        const divider = period === 'year' ? 1 : 12;
        return (car?.taxesCostsYearly * value) / divider;
      })
      .reduce((partial_sum, a) => partial_sum + a, 0);
  }

  return 0;
};

export const calculateTaxesIndiv = (
  cars: GatsbyTypes.ContentfulCars[],
  period: 'year' | 'month',
  answers?: i.Answers,
) => {
  const perCarPetrol = {
    carOne: 0,
    carTwo: 0,
    carThree: 0,
    carFour: 0,
    carFive: 0,
    carSix: 0,
    vanOne: 0,
    vanTwo: 0,
    vanThree: 0,
    vanFour: 0,
    vanFive: 0,
    vanSix: 0,
  };

  if (!answers) return perCarPetrol;
  const { fleet } = answers;

  if (fleet) {
    (Object.entries(fleet) as [i.KeyOfFleetInput, number][]).forEach(
      ([key, value]) => {
        const carPetrol = carSelector(
          cars,
          key as keyof i.FleetInput,
          'petrol',
        );

        if (!carPetrol?.taxesCostsYearly) return 0;
        const divider = period === 'year' ? 1 : 12;
        perCarPetrol[key] = (carPetrol?.taxesCostsYearly * value) / divider;
      },
    );
  }

  return perCarPetrol;
};
